import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useRouter from "common/hooks/use-router";
import useAuth from "common/hooks/useAuth";

import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import {
  // eslint-disable-next-line comma-dangle
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

import { Animal } from "types/Animal";
import AlertsContext from "common/providers/alerts";
import usePark from "common/hooks/usePark";
import { IoPawOutline } from "react-icons/io5";
import SpecySelect from "components/select/SpecySelect/SpecySelect";
import TagSelect from "components/select/TagSelect/TagSelect";
import EnclosureSelect from "components/select/EnclosureSelect/EnclosureSelect";
import SectorSelect from "components/select/SectorSelect/SectorSelect";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import AnimalItem from "./AnimalItem";

function Animals() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const { user: currentUser } = useAuth();
  const { park } = usePark();
  const canManageAnimals =
    (currentUser?.isAdmin ||
      currentUser?.isSuperadmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.animal?.create)) ??
    false;

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<Animal>("animals", {
    defaultFilters:
      currentUser?.isSuperadmin && park?.id
        ? {
            park: park.id,
          }
        : {},
    defaultSort: "houseName:ASC",
    cachePrefix: "Animals_all",
  });
  const { removeItem } = useItem<Animal>("animals", "");

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const handleNavigate = (id: any) => {
    navigate(`/animals/edit/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "houseName$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-1",
      },
      {
        Header: park?.hasZimsImport ? t("animals.localId") : t("animals.local"),
        filterName: "localId$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-1",
      },
      {
        Header: t("animals.tag"),
        filterName: "tag$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-1",
      },
      {
        Header: t("animals.transponderId"),
        filterName: "transponderId$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-1",
      },
      {
        Header: t("animals.band"),
        filterName: "band$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-1",
      },
      {
        Header: t("forms.specy"),
        filterName: "species",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <SpecySelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
      {
        Header: t("forms.enclosure"),
        filterName: "enclosures",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <EnclosureSelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
      {
        Header: t("forms.sector"),
        filterName: "sector",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <SectorSelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
      {
        Header: t("forms.tag"),
        filterName: "tags",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <TagSelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
      {
        Header: t("forms.isArchived"),
        filterName: "isArchived",
        filterComponent: (props: any) => (
          <div className="pt-2">
            <ToggleSwitch rightLabel={t("forms.isArchived")} {...props} />
          </div>
        ),
        // headerClasses: "mb-3",
      },
    ],
    [t, park?.hasZimsImport]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.animals")}
        subtitle={t("titleLegends.animals")}
        btnTitle={t("newItem.animal")}
        btnSubtitle={t("newItem.animalLegend")}
        Icon={IoPawOutline}
        addLink="/animals/add"
        addLinkDisabled={!canManageAnimals}
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={AnimalItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Animals;
